import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Autocomplete,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { iegmService } from '../../services/api';
import FaixaBadge from '../../components/iegm/FaixaBadge';
import RadarChart from '../../components/iegm/RadarChart';

const DIMENSOES = [
  { key: 'iamb', label: 'i-Amb', fullLabel: 'Meio Ambiente' },
  { key: 'icidade', label: 'i-Cidade', fullLabel: 'Cidade' },
  { key: 'ieduc', label: 'i-Educ', fullLabel: 'Educacao' },
  { key: 'ifiscal', label: 'i-Fiscal', fullLabel: 'Gestao Fiscal' },
  { key: 'igov_ti', label: 'i-GovTI', fullLabel: 'Governanca TI' },
  { key: 'isaude', label: 'i-Saude', fullLabel: 'Saude' },
  { key: 'iplan', label: 'i-Plan', fullLabel: 'Planejamento' },
];

const IegmDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { municipioAtivo } = useAuth();
  const [dados, setDados] = useState<any>(null);
  const [media, setMedia] = useState<any>(null);
  const [ranking, setRanking] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busca, setBusca] = useState('');
  const [comparar, setComparar] = useState<any>(null);

  const municipioId = municipioAtivo?.id;

  const carregar = useCallback(async () => {
    if (!municipioId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const [mun, med, rank] = await Promise.all([
        iegmService.getMunicipio(municipioId),
        iegmService.getMediaEstadual(municipioId),
        iegmService.getRanking(municipioId),
      ]);
      setDados(mun);
      setMedia(med);
      setRanking(rank);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao carregar dados IEGM');
    } finally {
      setLoading(false);
    }
  }, [municipioId]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const codigoIbge = municipioAtivo?.codigo_ibge;

  const radarData = dados
    ? DIMENSOES.map((d) => ({
        dimensao: d.label,
        percentual: parseFloat(dados['pct_' + d.key]) || 0,
        mediaEstadual: media ? parseFloat(media['pct_' + d.key]) || 0 : undefined,
      }))
    : [];

  const posicaoRanking = dados
    ? ranking.findIndex((r) => r.codigo_ibge === codigoIbge) + 1
    : 0;

  const rankingFiltrado = busca
    ? ranking.filter((r) =>
        r.municipio.toLowerCase().includes(busca.toLowerCase())
      )
    : ranking.slice(0, 20);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1400, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={700} gutterBottom>
          IEGM — Gestao Municipal
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Indice de Efetividade da Gestao Municipal — {municipioAtivo?.nome || 'Selecione um municipio'}
          {dados?.ano_ref && (' — ' + dados.ano_ref)}
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {!dados ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          Municipio nao encontrado na base IEGM. Verifique se o codigo IBGE ({codigoIbge}) esta correto
          ou se o municipio participou da avaliacao.
        </Alert>
      ) : (
        <>
          {/* Score Card + Radar */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            {/* Score principal */}
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%', textAlign: 'center', py: 3 }}>
                <CardContent>
                  <Typography variant="overline" color="text.secondary">
                    IEGM Geral
                  </Typography>
                  <Typography variant="h2" fontWeight={800} sx={{ my: 1 }}>
                    {((parseFloat(dados.pct_iegm) || 0) * 100).toFixed(1)}%
                  </Typography>
                  <FaixaBadge faixa={dados.ind_iegm} size="medium" />
                  {posicaoRanking > 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                      {posicaoRanking}o de {ranking.length} municipios
                    </Typography>
                  )}
                  {media && (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                      Media MG: {((parseFloat(media.pct_iegm) || 0) * 100).toFixed(1)}%
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Radar */}
            <Grid item xs={12} md={8}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    Perfil por Dimensao
                  </Typography>
                  <RadarChart data={radarData} showMedia={!!media} />
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* 7 Cards de dimensao */}
          <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
            Dimensoes
          </Typography>
          <Grid container spacing={2} sx={{ mb: 4 }}>
            {DIMENSOES.map((dim) => {
              const pct = parseFloat(dados['pct_' + dim.key]) || 0;
              const ind = dados['ind_' + dim.key] || '-';
              const mediaPct = media ? parseFloat(media['pct_' + dim.key]) || 0 : null;
              const isSaude = dim.key === 'isaude';
              return (
                <Grid item xs={12} sm={6} md={3} key={dim.key}>
                  <Card
                    sx={{
                      cursor: isSaude ? 'pointer' : 'default',
                      transition: 'box-shadow 0.2s',
                      '&:hover': isSaude ? { boxShadow: 4 } : {},
                      border: isSaude ? '2px solid #3b82f6' : undefined,
                    }}
                    onClick={isSaude ? () => navigate('/iegm/isaude') : undefined}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="subtitle2" fontWeight={600}>
                          {dim.label}
                        </Typography>
                        <FaixaBadge faixa={ind} />
                      </Box>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                        {dim.fullLabel}
                      </Typography>
                      <Typography variant="h5" fontWeight={700}>
                        {(pct * 100).toFixed(1)}%
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(pct * 100, 100)}
                        sx={{
                          mt: 1,
                          height: 6,
                          borderRadius: 3,
                          backgroundColor: '#e2e8f0',
                          '& .MuiLinearProgress-bar': {
                            borderRadius: 3,
                            backgroundColor: pct >= 0.75 ? '#16a34a' : pct >= 0.5 ? '#eab308' : pct >= 0.25 ? '#f97316' : '#ef4444',
                          },
                        }}
                      />
                      {mediaPct !== null && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                          Media MG: {(mediaPct * 100).toFixed(1)}%
                        </Typography>
                      )}
                      {isSaude && (
                        <Typography variant="caption" color="primary" sx={{ mt: 0.5, display: 'block' }}>
                          Clique para detalhes I-Saude
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </>
      )}

      {/* Ranking */}
      <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
        Ranking IEGM — Municipios MG
      </Typography>
      <Box sx={{ mb: 2 }}>
        <TextField
          size="small"
          placeholder="Buscar municipio..."
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          sx={{ width: 320 }}
        />
      </Box>
      <TableContainer component={Paper} sx={{ maxHeight: 500 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>#</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Municipio</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="center">IEGM</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="center">Faixa</TableCell>
              {DIMENSOES.map((d) => (
                <TableCell key={d.key} sx={{ fontWeight: 600 }} align="center">
                  {d.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rankingFiltrado.map((r, idx) => {
              const pos = ranking.indexOf(r) + 1;
              const isAtivo = r.codigo_ibge === codigoIbge;
              return (
                <TableRow
                  key={r.codigo_ibge}
                  sx={{
                    backgroundColor: isAtivo ? 'rgba(37,99,235,0.08)' : undefined,
                    fontWeight: isAtivo ? 700 : 400,
                  }}
                >
                  <TableCell>{pos}</TableCell>
                  <TableCell sx={{ fontWeight: isAtivo ? 700 : 400 }}>{r.municipio}</TableCell>
                  <TableCell align="center">
                    {((parseFloat(r.pct_iegm) || 0) * 100).toFixed(1)}%
                  </TableCell>
                  <TableCell align="center">
                    <FaixaBadge faixa={r.ind_iegm} />
                  </TableCell>
                  {DIMENSOES.map((d) => (
                    <TableCell key={d.key} align="center">
                      <FaixaBadge faixa={r['ind_' + d.key]} />
                    </TableCell>
                  ))}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      {!busca && ranking.length > 20 && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Exibindo top 20 de {ranking.length}. Use a busca para encontrar outros municipios.
        </Typography>
      )}
    </Box>
  );
};

export default IegmDashboard;
